import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'react-router-redux';

import {BOOKING_DETAIL_URL} from 'config/urls';

import Page from 'components/Page';
import {Container} from 'containers/types';
import Loading from 'components/Loading';
import BookingCreateModal from 'components/BookingCreateModal';
import ListContent from 'components/ListContent';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';
import BookingNotesModal from 'components/BookingNotesModal';

import {BookingsTable, BookingListFilters, SubNavigation} from './components';

import {
  fetchBookings,
  fetchBookingTypes,
  selectBookingTypes,
  fetchBookingOrganisations,
  selectBookingOrganisations,
  fetchBookingListFilters,
  selectBookingsFilters,
  selectBookings,
  selectBookingsLoading,
  fetchSalesAgents,
  selectSalesAgent,
  fetchGoCardlessMandateStatuses,
  selectGoCardlessMandateStatuses,
  fetchGoCardlessInvitationStatuses,
  selectGoCardlessInvitationStatuses
} from 'ducks/bookings';
import {
  fetchFloors,
  selectFloors,
  fetchAllRooms,
  selectAllRooms
} from 'ducks/rooms';
import {
  openModal,
  isModalOpen,
  closeModal,
  MODALS as BASE_MODALS
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {exportCsv, exportCsvInBackground, selectExporting} from 'ducks/files';
import {
  exportBookingsCsvInBackground as exportBookingsCsvInBackgroundSdk,
  exportBookingsWithoutFinancialDataCsvInBackground as exportBookingsWithoutFinancialDataCsvInBackgroundSdk,
  exportContactsCsvInBackground as exportContactsCsvInBackgroundSdk
} from 'sdk/bookings';
import {composeActions} from 'ducks/common';

import {reverse} from 'utils/urls';

import './styles.css';

const BOOKINGS_CSV_FILENAME = 'bookings';
const CONTACTS_CSV_FILENAME = 'contacts';

const initialFilters: {
  search: string;
  bookingTypes: string[];
  bookingStatuses: string[];
  rooms: number[];
  roomTypes: string[];
  dummyRooms: boolean;
  isRenewal: boolean;
  hasCompletedAgreement: boolean;
  floors: string[];
  arrivalStart: string;
  arrivalEnd: string;
  departureStart: string;
  departureEnd: string;
  createdStart: string;
  createdEnd: string;
  stayStart: string;
  stayEnd: string;
  capacity: number | null;
  createdBy: number[];
  salesAgents: string[];
  verificationMethod: string | null;
  bookingOrganisations: string[];
  gocardlessMandateStatuses: string[];
  gocardlessInvitationStatuses: string[];
  hasEarlyTermination: boolean;
} = {
  search: '',
  bookingTypes: [],
  bookingStatuses: [],
  rooms: [],
  roomTypes: [],
  dummyRooms: false,
  isRenewal: false,
  hasCompletedAgreement: false,
  floors: [],
  arrivalStart: '',
  arrivalEnd: '',
  departureStart: '',
  departureEnd: '',
  createdStart: '',
  createdEnd: '',
  stayStart: '',
  stayEnd: '',
  capacity: null,
  createdBy: [],
  salesAgents: [],
  verificationMethod: null,
  bookingOrganisations: [],
  gocardlessMandateStatuses: [],
  gocardlessInvitationStatuses: [],
  hasEarlyTermination: false
};

class BookingList extends React.Component<
  Container &
    WithSortingProps &
    WithPaginationProps &
    WithFiltersProps<typeof initialFilters> &
    LoginRequiredProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {locationRef} = this.props.match.params;

    // TODO: Add permissions
    this.props.fetchBookings({locationRef});

    this.props.fetchSalesAgents({locationRef});
    this.props.fetchFloors({locationRef});
    this.props.fetchBookingTypes();
    this.props.fetchBookingOrganisations();
    this.props.fetchAllRooms({locationRef});
    this.props.fetchBookingListFilters({locationRef});
    this.props.fetchGoCardlessMandateStatuses();
    this.props.fetchGoCardlessInvitationStatuses();
  }

  onPageChange = ({offset, pageSize}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({pageSize, offset}, [
      () => fetchBookings({locationRef})
    ]);
  };

  openBookingCreateModal = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_BOOKING
      },
      () => this.props.openModal(BASE_MODALS.CREATE_BOOKING)
    );

  exportBookingsCsvInBackground = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef,
            filters: this.props.getFilters(),
            sort: this.props.getSort()
          },
          exportSdk: exportBookingsCsvInBackgroundSdk,
          fileName: BOOKINGS_CSV_FILENAME
        })
    );
  };

  exportBookingsWithoutFinancialDataCsvInBackground = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef,
            filters: this.props.getFilters(),
            sort: this.props.getSort()
          },
          exportSdk: exportBookingsWithoutFinancialDataCsvInBackgroundSdk,
          fileName: BOOKINGS_CSV_FILENAME
        })
    );
  };

  exportContactsCsvInBackground = () => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef,
            filters: this.props.getFilters()
          },
          exportSdk: exportContactsCsvInBackgroundSdk,
          fileName: CONTACTS_CSV_FILENAME
        })
    );
  };

  onSort = fieldName => {
    const {locationRef} = this.props.match.params;
    const pagination = this.props.getPagination();

    this.props.onSort(fieldName, [
      () =>
        this.props.onPaginationChange({
          pageSize: pagination.pageSize,
          offset: 0
        }),
      () => fetchBookings({locationRef})
    ]);
  };

  // used to overwrite a certain filter key with the value passed
  setFilter = (field, value) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.setFilter(field, value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchBookings({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchBookings({locationRef})
    ]);
  };

  fetchBookingsExtraActions = () => {
    const {locationRef} = this.props.match.params;

    return [() => fetchBookings({locationRef})];
  };

  openBookingNotesModal = ({bookingId}: any) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_BOOKING_NOTES,
        locationRef
      },
      () => {
        this.props.openModal(BASE_MODALS.BOOKING_NOTES, {
          bookingId,
          locationRef,
          refetchExtraAction: this.fetchBookingsExtraActions
        });
      }
    );
  };

  redirectToBooking = ({bookingId}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        this.props.push(
          reverse(BOOKING_DETAIL_URL, {
            locationRef,
            bookingId
          })
        );
      }
    );
  };

  createBookingExtraActions = () => [
    () => closeModal(BASE_MODALS.CREATE_BOOKING),
    ({bookingId}) =>
      push(
        reverse(BOOKING_DETAIL_URL, {
          locationRef: this.props.match.params.locationRef,
          bookingId
        })
      )
  ];

  render() {
    const {
      match: {params: {locationRef}},
      bookings,
      floors,
      bookingTypes,
      rooms,
      bookingCreateModal,
      isLoading,
      bookingListFilters,
      bookingNotesModal,
      bookingOrganisations,
      salesAgents,
      gocardlessMandateStatuses,
      gocardlessInvitationStatuses
    } = this.props;

    return (
      <Page activeTab="bookings" locationRef={locationRef}>
        <ListContent
          className="BookingList"
          filters={this.props.getFilters()}
          setFilter={this.setFilter}
          initialFilters={initialFilters}
          navigationExtraElement={SubNavigation}>
          <ListContent.Content>
            {isLoading && <Loading />}
            {!isLoading && (
              <BookingsTable
                bookings={bookings}
                pagination={this.props.getPagination()}
                onPageChange={this.onPageChange}
                sort={this.props.getSort()}
                onSort={this.onSort}
                exportBookingsCsvInBackground={
                  this.exportBookingsCsvInBackground
                }
                exportBookingsWithoutFinancialDataCsvInBackground={
                  this.exportBookingsWithoutFinancialDataCsvInBackground
                }
                exportContactsCsvInBackground={
                  this.exportContactsCsvInBackground
                }
                exportingBookingsCsv={this.props.exportingBookingsCsv}
                exportingContactsCsv={this.props.exportingContactsCsv}
                openBookingNotesModal={this.openBookingNotesModal}
                redirectToBooking={this.redirectToBooking}
              />
            )}
          </ListContent.Content>
          <ListContent.Filters>
            <BookingListFilters
              locationRef={locationRef}
              setFilter={this.setFilter}
              getFilters={this.props.getFilters}
              clearFilters={this.clearFilters}
              rooms={rooms}
              floors={floors}
              bookingTypes={bookingTypes}
              salesAgents={salesAgents}
              bookingListFilters={bookingListFilters}
              bookingOrganisations={bookingOrganisations}
              gocardlessMandateStatuses={gocardlessMandateStatuses}
              gocardlessInvitationStatuses={gocardlessInvitationStatuses}
            />
          </ListContent.Filters>
        </ListContent>

        {bookingNotesModal && <BookingNotesModal />}

        {bookingCreateModal && (
          <BookingCreateModal
            locationRef={locationRef}
            createBookingExtraActions={this.createBookingExtraActions}
          />
        )}
      </Page>
    );
  }
}

const mapStateToProps = state => {
  return {
    bookings: selectBookings(state),
    salesAgents: selectSalesAgent(state),
    floors: selectFloors(state),
    bookingTypes: selectBookingTypes(state),
    bookingOrganisations: selectBookingOrganisations(state),
    rooms: selectAllRooms(state),
    exportingBookingsCsv: selectExporting(state, BOOKINGS_CSV_FILENAME),
    exportingContactsCsv: selectExporting(state, CONTACTS_CSV_FILENAME),
    isLoading: selectBookingsLoading(state),
    bookingCreateModal: isModalOpen(state, BASE_MODALS.CREATE_BOOKING),
    bookingListFilters: selectBookingsFilters(state),
    bookingNotesModal: isModalOpen(state, BASE_MODALS.BOOKING_NOTES),
    gocardlessMandateStatuses: selectGoCardlessMandateStatuses(state),
    gocardlessInvitationStatuses: selectGoCardlessInvitationStatuses(state)
  };
};

const mapDispatchToProps = {
  fetchBookings,
  fetchSalesAgents,
  openModal,
  fetchFloors,
  fetchBookingTypes,
  fetchBookingOrganisations,
  fetchAllRooms,
  requiresPermission,
  exportCsv,
  exportCsvInBackground,
  fetchBookingListFilters,
  composeActions,
  push,
  fetchGoCardlessMandateStatuses,
  fetchGoCardlessInvitationStatuses
};

const componentWithSorting = withSorting(
  LoginRequired(BookingList),
  SORTING.BOOKING_LIST,
  {saveToUrl: true, clearSortingOnUnmount: false}
);
const componentWithSortingAndPagination = withPagination(
  componentWithSorting,
  PAGINATION.BOOKING_LIST,
  {saveToUrl: true}
);
const componentWithSortingPaginationAndFilters = withFilters(
  componentWithSortingAndPagination,
  FILTERS.BOOKING_LIST,
  {initialFilters, saveToUrl: true, clearFiltersOnUnmount: false}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  componentWithSortingPaginationAndFilters
);
